import React from "react";
import { Accept, useDropzone } from "react-dropzone";
import classNames from "classnames";

import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as UploadIcon } from "src/images/upload-file.svg";
import { ReactComponent as CrossIcon } from "src/images/cross.svg";
import { ReactComponent as LoaderDots } from "src/images/loader-dots.svg";

import "./FileDropzone.scss";
import { showErrorToast } from "src/utils/methods";

interface Props {
  className?: string;
  files?: File[];
  accept: Accept;
  multiple?: boolean;
  onChange: (files: File[]) => void;
  maxSizeMB?: number;
  loading?: boolean;
}

// Displaying files, or information about uploaded files is not supported now as it was not designed.
// It is not needed for now. Don't hesitate to add it if you need it.
export const FileDropzone = (props: Props) => {
  const {
    className,
    files = [],
    accept,
    multiple = false,
    onChange,
    maxSizeMB,
    loading,
  } = props;

  const formats = Object.values(accept).flat().join(", ");
  const maxSizeBytes = maxSizeMB ? maxSizeMB * 1024 * 1024 : undefined;

  const handleDrop = (acceptedFiles: File[]) => {
    const newFiles = multiple ? [...files, ...acceptedFiles] : acceptedFiles;
    onChange(newFiles);
  };

  const handleDropRejected = () => {
    showErrorToast({
      id: "ws_invalid_file_type_or_size",
      defaultMessage: "Invalid file type or size",
    });
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: handleDrop,
      onDropRejected: handleDropRejected,
      accept,
      multiple,
      maxSize: maxSizeBytes,
    });

  return (
    <div
      {...getRootProps()}
      style={{
        textAlign: "center",
        transition: "0.2s",
      }}
      className={classNames(
        "file-dropzone",
        {
          "file-dropzone--drag-active": isDragActive,
          "file-dropzone--loading": loading,
          "file-dropzone--drag-reject": isDragReject,
        },
        className,
      )}
    >
      <input {...getInputProps()} />

      {loading ? (
        <span className="file-dropzone__loader">
          <LoaderDots width={20} height={20} />
          <span>
            <IDHFormattedMessage
              id="ws_uploading_file"
              defaultMessage="Uploading file"
            />
            ...
          </span>
        </span>
      ) : (
        <>
          {isDragReject ? <CrossIcon /> : <UploadIcon />}
          <p className="file-dropzone__text">
            {isDragReject ? (
              <IDHFormattedMessage
                id="ws_invalid_file_type_or_size"
                defaultMessage="Invalid file type or size"
              />
            ) : isDragActive ? (
              <IDHFormattedMessage
                id="ws_drop_file_here"
                defaultMessage="Drop file here"
              />
            ) : (
              <IDHFormattedMessage
                id="ws_drag_&_drop_a_file_here_or_click_to_upload"
                defaultMessage="Drag & drop a file here or click to upload"
              />
            )}
          </p>
          <p className="file-dropzone__info">
            {!multiple && (
              <>
                <span>
                  1 <IDHFormattedMessage id="ws_file" defaultMessage="file" />
                </span>
                •
              </>
            )}
            <span>
              <IDHFormattedMessage id="ws_formats" defaultMessage="Formats" />:{" "}
              {formats.toUpperCase()}
            </span>
            {maxSizeMB && (
              <>
                •
                <span>
                  <IDHFormattedMessage
                    id="ws_max_file_size"
                    defaultMessage="Max file size"
                  />
                  : {maxSizeMB}MB
                </span>
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
};
